/* Humbler home — sections/humbler-home-*.liquid. Loaded with `defer` by each section that needs it;
   the guard below keeps it to one run per page.
   1. Scrollers ([data-hb-home-scroller]): progress bar ([data-hb-home-progress], hidden when nothing
      overflows) and the desktop prev/next arrows of the tab section.
   2. Tabs ([data-hb-home-tabs]): plain links without JS, ARIA tabs with arrow-key navigation with it. */
(() => {
  if (window.HumblerHomeScrollers) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- scrollers ---------- */
  const update = scroller => {
    const overflow = scroller.scrollWidth - scroller.clientWidth > 4;
    const bar = scroller.parentElement.querySelector(':scope > [data-hb-home-progress]');
    if (bar) {
      bar.hidden = !overflow;
      const span = bar.firstElementChild;
      const width = Math.min(100, (scroller.clientWidth / scroller.scrollWidth) * 100);
      span.style.setProperty('--_width', `${width}%`);
      span.style.setProperty('--_offset', `${(scroller.scrollLeft / scroller.clientWidth) * 100}%`);
    }
    const section = scroller.closest('[data-hb-home-tabs]');
    if (section && !scroller.closest('[hidden]')) {
      const arrows = section.querySelector('[data-hb-arrows]');
      if (arrows) {
        arrows.hidden = !overflow;
        arrows.querySelector('[data-hb-arrow="-1"]').disabled = scroller.scrollLeft < 4;
        arrows.querySelector('[data-hb-arrow="1"]').disabled = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4;
      }
    }
  };
  const watch = scroller => {
    if (scroller.dataset.hbWatched) return;
    scroller.dataset.hbWatched = 'true';
    let frame = 0;
    scroller.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => update(scroller)); }, { passive: true });
    new ResizeObserver(() => update(scroller)).observe(scroller);
    update(scroller);
  };
  document.addEventListener('click', event => {
    const arrow = event.target.closest('[data-hb-arrow]');
    if (!arrow) return;
    const scroller = arrow.closest('[data-hb-home-tabs]')?.querySelector('[data-hb-panel]:not([hidden]) [data-hb-home-scroller]');
    if (!scroller) return;
    scroller.scrollBy({ left: Number(arrow.dataset.hbArrow) * scroller.clientWidth * 0.9, behavior: reduce.matches ? 'auto' : 'smooth' });
  });

  /* ---------- tabs ---------- */
  const enhance = section => {
    const list = section.querySelector('[data-hb-tablist]');
    if (!list || list.getAttribute('role') === 'tablist') return;
    const tabs = [...list.querySelectorAll('[data-hb-tab]')];
    const all = section.querySelector('[data-hb-tabs-all]');
    list.setAttribute('role', 'tablist');
    const select = (tab, focus) => {
      tabs.forEach(item => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
        if (active) item.setAttribute('aria-current', 'true'); else item.removeAttribute('aria-current');
        const panel = document.getElementById(item.dataset.hbTab);
        if (panel) panel.hidden = !active;
      });
      if (all) all.href = tab.dataset.href;
      if (focus) tab.focus();
      const scroller = document.getElementById(tab.dataset.hbTab)?.querySelector('[data-hb-home-scroller]');
      if (scroller) { scroller.scrollLeft = 0; update(scroller); }
    };
    tabs.forEach(tab => {
      const panel = document.getElementById(tab.dataset.hbTab);
      tab.dataset.href = tab.getAttribute('href');
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', tab.dataset.hbTab);
      if (panel) { panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', tab.id); }
      // Hidden panels' lazy cards only load once shown: intent to switch (hover, touch, focus) starts
      // the first row early, so the tab opens on photos rather than empty stages.
      const warm = () => {
        const images = panel ? panel.querySelectorAll('img[loading="lazy"]:not(.hb-card__image--alt)') : [];
        if (window.HumblerSkeleton) window.HumblerSkeleton.warm(images, 4);
        else [...images].slice(0, 4).forEach(img => { img.loading = 'eager'; });
      };
      ['pointerenter', 'touchstart', 'focus'].forEach(type => tab.addEventListener(type, warm, { once: true, passive: true }));
      tab.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        select(tab, false);
      });
      tab.addEventListener('keydown', event => {
        const index = tabs.indexOf(tab);
        const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        select(tabs[(next + tabs.length) % tabs.length], true);
      });
    });
    select(tabs.find(tab => tab.hasAttribute('aria-current')) || tabs[0], false);
  };

  const scan = (scope = document) => {
    scope.querySelectorAll('[data-hb-home-tabs]').forEach(enhance);
    scope.querySelectorAll('[data-hb-home-scroller]').forEach(watch);
  };
  window.HumblerHomeScrollers = { scan, update };
  document.addEventListener('shopify:section:load', event => scan(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
})();
